import localforage from 'localforage';
import { encryptPayload, decryptPayload } from './encryptionKey';

const collections = [
    'sales', 'inventory', 'expenses', 'invoices', 'brands', 'categories', 'suppliers', 'queue',
    'movements', 'purchases', 'customers', 'receipts', 'payments', 'dashboard',
    // Master data collections (for local-first loading)
    'master_brands', 'master_categories', 'master_products', 'master_suppliers',
    'master_customers', 'master_invoices', 'master_receipts',
    'sync_metadata',
];

const stores = collections.reduce((acc, name) => {
    acc[name] = localforage.createInstance({
        name: 'tauris-offline',
        storeName: `tauris_${name}`,
    });
    return acc;
}, {});

const encrypt = (data) => encryptPayload(data);
const decrypt = (cipher) => decryptPayload(cipher);

const withMeta = (record) => {
    const now = new Date().toISOString();
    return {
        localId: record.localId || crypto.randomUUID(),
        synced: record.synced ?? false,
        syncStatus: record.syncStatus || (record.synced ? 'SYNCED' : 'PENDING'),
        createdAt: record.createdAt || now,
        updatedAt: now,
        _version: record._version || 1,
        ...record,
    };
};

export const saveRecord = async (collection, record) => {
    const store = stores[collection];
    if (!store) throw new Error(`Unknown collection: ${collection}`);
    const enriched = withMeta(record);
    await store.setItem(enriched.localId, encrypt(enriched));
    return enriched;
};

export const getAllRecords = async (collection) => {
    const store = stores[collection];
    if (!store) throw new Error(`Unknown collection: ${collection}`);
    const results = [];
    await store.iterate((value) => {
        const decoded = decrypt(value);
        if (decoded) results.push(decoded);
    });
    return results;
};

export const getUnsyncedRecords = async (collection) => {
    const all = await getAllRecords(collection);
    return all.filter((r) => !r.synced);
};

export const markRecordSynced = async (collection, localId, serverId) => {
    const store = stores[collection];
    if (!store) throw new Error(`Unknown collection: ${collection}`);
    const existing = decrypt(await store.getItem(localId));
    if (!existing) return;
    const updated = {
        ...existing,
        synced: true,
        syncStatus: 'SYNCED',
        serverId: serverId || existing.serverId,
        updatedAt: new Date().toISOString(),
    };
    await store.setItem(localId, encrypt(updated));
};

export const updateRecordSyncStatus = async (collection, localId, syncStatus, extra = {}) => {
    const store = stores[collection];
    if (!store) throw new Error(`Unknown collection: ${collection}`);
    const existing = decrypt(await store.getItem(localId));
    if (!existing) return;
    const updated = {
        ...existing,
        ...extra,
        syncStatus,
        updatedAt: new Date().toISOString(),
    };
    await store.setItem(localId, encrypt(updated));
};

export const enqueueOperation = async (operation) => {
    const store = stores.queue;
    const now = new Date().toISOString();
    const op = {
        id: operation.id || crypto.randomUUID(),
        synced: false,
        status: operation.status || 'PENDING',
        timestamp: operation.timestamp || now,
        ...operation,
    };
    await store.setItem(op.id, encrypt(op));
    return op;
};

export const getPendingOperations = async () => {
    const store = stores.queue;
    const results = [];
    await store.iterate((value) => {
        const decoded = decrypt(value);
        if (decoded && !decoded.synced && decoded.status !== 'CANCELLED') {
            results.push(decoded);
        }
    });
    results.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return results;
};

/** All queue ops including synced/failed/cancelled (for Offline Queue UI). */
export const getAllOperations = async () => {
    const store = stores.queue;
    const results = [];
    await store.iterate((value) => {
        const decoded = decrypt(value);
        if (decoded) results.push(decoded);
    });
    results.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return results;
};

export const markOperationSynced = async (id) => {
    const store = stores.queue;
    const op = decrypt(await store.getItem(id));
    if (!op) return;
    await store.setItem(id, encrypt({
        ...op,
        synced: true,
        status: 'SYNCED',
        syncedAt: new Date().toISOString(),
    }));
};

export const markOperationFailed = async (id, errorMessage) => {
    const store = stores.queue;
    const op = decrypt(await store.getItem(id));
    if (!op) return;
    await store.setItem(id, encrypt({
        ...op,
        synced: false,
        status: 'FAILED',
        lastError: errorMessage || 'Sync failed',
        failedAt: new Date().toISOString(),
    }));
};

export const cancelOperation = async (id) => {
    const store = stores.queue;
    const op = decrypt(await store.getItem(id));
    if (!op) return null;
    if (op.synced) return op;
    const updated = {
        ...op,
        synced: true,
        status: 'CANCELLED',
        cancelledAt: new Date().toISOString(),
    };
    await store.setItem(id, encrypt(updated));
    if (op.recordCollection && op.localId) {
        await updateRecordSyncStatus(op.recordCollection, op.localId, 'CANCELLED');
    }
    return updated;
};

export const requeueOperation = async (id) => {
    const store = stores.queue;
    const op = decrypt(await store.getItem(id));
    if (!op) return null;
    const updated = {
        ...op,
        synced: false,
        status: 'PENDING',
        lastError: undefined,
        failedAt: undefined,
    };
    await store.setItem(id, encrypt(updated));
    if (op.recordCollection && op.localId) {
        await updateRecordSyncStatus(op.recordCollection, op.localId, 'PENDING');
    }
    return updated;
};

export const getPendingCount = async () => {
    const pending = await getPendingOperations();
    return pending.length;
};

export const clearOfflineData = async () => {
    await Promise.all(collections.map((name) => stores[name].clear()));
};

/** Estimate IndexedDB usage; warn when approaching quota. */
export const getStorageEstimate = async () => {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
        return { usage: 0, quota: 0, usageRatio: 0, warn: false };
    }
    try {
        const { usage = 0, quota = 0 } = await navigator.storage.estimate();
        const usageRatio = quota > 0 ? usage / quota : 0;
        return {
            usage,
            quota,
            usageRatio,
            warn: usageRatio >= 0.8,
            critical: usageRatio >= 0.95,
        };
    } catch {
        return { usage: 0, quota: 0, usageRatio: 0, warn: false };
    }
};

export const OFFLINE_COLLECTIONS = collections;
